import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ProfileMin } from '../../../../profiles/shared/profile-min.model';
import { ProfileRead } from '../../../../profiles/shared/profile-read.model';
import { ProfileAddress } from '../../../../profiles/shared/profileAddress.model';
import { ProfileService } from '../../../../profiles/shared/profile.service';
import { UtilService, DialogService, PagedResultGeneric } from '../../../../shared/index';
import { EventRead } from '../../../shared/event-read.model';
import { IValidationComponent } from '../../../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { ProfileDupCheckerComponent } from '../../../../profiles/profile-dup-checker/profile-dup-checker.component';
import { EventV4RegistrationService } from 'src/app/events-v4/shared/registration/event-v4-registration.service';
import { RegistrationUpsert } from 'src/app/events-v4/shared/registration/crud/registration-upsert.model';
import { EventRegistrationActionTrackerService } from 'src/app/events-v4/shared/event-registration-action-tracker.service';
import { EventRegistationActionTypeEnum } from 'src/app/events-v4/shared/dto/event-registation-action-type.enum';
import { EventRegistrationEditActionDto } from 'src/app/events-v4/shared/dto/event-registration-edit-action.dto';
import { EmailInputComponent } from '../../../../shared/widgets/email-input/email-input.component';

@Component({
  selector: 'wl-event-v4-registration-wizard-contact',
  templateUrl: 'event-v4-registration-wizard-contact.component.html',
  standalone: false,
})
export class EventV4RegistrationWizardContactComponent implements IValidationComponent {
  public registrationId: number;
  public _registration: RegistrationUpsert = new RegistrationUpsert();

  @Input() public get registration(): RegistrationUpsert {
    return this._registration;
  }
  public set registration(value: RegistrationUpsert) {
    this._registration = value;
    if (value && value.Registration) this._copiedValuesProfileId = value.Registration.ProfileId;
  }

  public event: EventRead = new EventRead();
  public initialLoadComplete: boolean = false;

  @ViewChild('profileOrgDupChecker', { static: false }) profileOrgDupChecker: ProfileDupCheckerComponent;
  @ViewChild('profileIndDupChecker', { static: false }) profileIndDupChecker: ProfileDupCheckerComponent;
  @ViewChild('emailInputComponent', { static: false }) emailFieldInput: EmailInputComponent;

  @Output() onRegistrationProfileChanged: EventEmitter<ProfileMin>;

  public _utilService: UtilService;
  public _translationService: TranslationService;
  public _registrationService: EventV4RegistrationService;
  public _checkingMainContact = false;
  public _checkingBillingContact = false;
  public _copiedValuesProfileId: number = 0;
  public _lastChosenProfile: ProfileMin = null;

  public constructor(
    utilService: UtilService,
    translationService: TranslationService,
    registrationService: EventV4RegistrationService,
    private actionTrackerService: EventRegistrationActionTrackerService,
    public _profileService: ProfileService
  ) {
    this._utilService = utilService;
    this._translationService = translationService;
    this._registrationService = registrationService;

    this.onRegistrationProfileChanged = new EventEmitter<ProfileMin>();

    this.registrationId = 0;
    this.initialLoadComplete = false;
  }

  public loadData = (): void => {
    this.initialLoadComplete = true;
    this._checkingMainContact = false;
  };

  public getValidationResult = (): ValidationResult => {
    let valid = new ValidationResult();
    valid.isValid = true;

    if (
      this._utilService.isNotNullEmptyOrWhitespace(this.registration.Registration.Email) ||
      this._utilService.isNotNullEmptyOrWhitespace(this.registration.Registration.FName) ||
      this._utilService.isNotNullEmptyOrWhitespace(this.registration.Registration.LName) ||
      this._utilService.isNotNullEmptyOrWhitespace(this.registration.Registration.OrgName)
    ) {
      valid.isValid = true;
    } else {
      valid.isValid = false;
      valid.errors.push(
        this._translationService.getInstantCapitalizeFirstCharacter(
          'events.registration.wizard.contact.add-registration-name-validation-error'
        )
      );
    }

    if (this.displayField(this.event.RegEmailVisible)) {
      if (
        this._utilService.isNotNullEmptyOrWhitespace(this.registration.Registration.Email) &&
        !this.emailFieldInput?.isValidEmail(this.registration.Registration.Email)
      ) {
        valid.isValid = false;
        valid.errors.push(this._translationService.getInstantCapitalizeFirstCharacter('common.personal-information.email-invalid-message'));
      }
    }

    // 2026 02 11 - for now we are not enforcing email required
    // if (this.event.RegEmailRequired && this._utilService.isNullEmptyOrWhitespace(this.registration.Registration.Email)) {
    //   valid.isValid = false;
    //   valid.errors.push(this._translationService.getInstantCapitalizeFirstCharacter('common.personal-information.email-required-message'));
    // }

    return valid;
  };

  public registrationProfileChanged = (profile: ProfileMin, force: boolean = false): void => {
    this._lastChosenProfile = profile;

    // membership gets recorded and copied down no matter what.
    let promises: Promise<any>[] = [];

    if (this.initialLoadComplete == true) {
      this.registration.Registration.IsMember = profile.Member;

      // check related profiles for if they're related to a member
      if (!profile.Member && profile.ProfileId > 0) {
        promises.push(
          this._profileService.checkIsPrimarilyRelatedToMember(profile.ProfileId).then((isRelatedToMember: boolean) => {
            this.registration.Registration.IsRelatedToMember = isRelatedToMember;
            return isRelatedToMember;
          })
        );
      } else {
        this.registration.Registration.IsRelatedToMember = false;

        // force triggers a profile change event
        promises.push(Promise.resolve(true));
      }
    }

    if (
      this.initialLoadComplete == true &&
      (force || !this.registration.IsEdit || profile.ProfileId != this.registration.Registration.ProfileId)
    ) {
      this._copiedValuesProfileId = this.registration.Registration.ProfileId = profile.ProfileId;
      this.registration.Registration.OrgName = profile.OrgName;

      // change first/last names if this is a new registration and the selected profile is an indiv
      if (!this.registration.IsEdit || !profile.OrgInd) {
        this.registration.Registration.Prefix = profile.Prefix;
        this.registration.Registration.Title = profile.Title;
        this.registration.Registration.FName = profile.FirstName;
        this.registration.Registration.LName = profile.LastName;

        this.registration.Registration.Email = profile.Email;
      }

      this.registration.Registration.Phone = profile.WorkPhone;
      if (this._utilService.isNullEmptyOrWhitespace(this.registration.Registration.Phone)) {
        this.registration.Registration.Phone = profile.CellPhone;
      }
      if (this._utilService.isNullEmptyOrWhitespace(this.registration.Registration.Phone)) {
        this.registration.Registration.Phone = profile.HomePhone;
      }

      // helper to copy address info
      const copyProfileAddressToRegistration = (p: ProfileMin) => {
        this.registration.Registration.Address1 = p.Address1;
        this.registration.Registration.Address2 = p.Address2;
        this.registration.Registration.City = p.City;
        this.registration.Registration.State = p.State;
        this.registration.Registration.Zip = p.Zip;
      };

      // get billing profile and use it for the address
      this._checkingBillingContact = true;

      this._profileService.getProfileBillingAddress(profile.ProfileId).then(
        (billingAddress: ProfileAddress) => {
          // billing contact loaded
          if (billingAddress && billingAddress.ProfileID > 0) copyProfileAddressToRegistration(<ProfileMin>(<any>billingAddress));
          else copyProfileAddressToRegistration(profile);

          this._checkingBillingContact = false;
        },
        () => {
          // fail quietly, just use this profile's address
          copyProfileAddressToRegistration(profile);
          this._checkingBillingContact = false;
        }
      );

      let relatedProfilePromise: Promise<ProfileRead> = Promise.resolve(null);
      this.registration.Registration.profileOrgInd = profile.OrgInd;

      // load the main contact and preselect it
      if (force || !this.registration.IsEdit) {
        if (profile.ProfileId > 0) {
          this._checkingMainContact = true;

          // preselect the main contact or main org, based on whether chosen profile is an or ror individual
          if (profile.OrgInd) relatedProfilePromise = this._profileService.getProfileMainContact(profile.ProfileId);
          else relatedProfilePromise = this._profileService.getMainOrFirstRelatedOrg(profile.ProfileId);

          relatedProfilePromise
            .then((mainContact: ProfileRead) => {
              if (mainContact && mainContact.ProfileId > 0) {
                if (!this.registration.IsEdit) this.registration.Registration.relatedProfileId = mainContact.ProfileId;

                if (!this.registration.IsEdit && profile.OrgInd && mainContact.Email)
                  this.registration.Registration.Email = mainContact.Email;

                // change first/last names if this is a new registration and the selected profile is an indiv
                if (!this.registration.IsEdit || !profile.OrgInd) {
                  this.relatedProfileChanged(<ProfileMin>(<any>mainContact));
                } else {
                  this.registration.Registration.CommunicateWithProfileId = profile.ProfileId;
                }
              } else if (!this.registration.IsEdit) {
                // reset only for new registration
                this.registration.Registration.relatedProfileId = 0;
                this.relatedProfileChanged(new ProfileMin());
              }

              this._checkingMainContact = false;

              return mainContact;
            })
            .catch(() => (this._checkingMainContact = false));
        } else if (!this.registration.IsEdit) {
          // reset only for new registration
          this.registration.Registration.relatedProfileId = 0;
          this.relatedProfileChanged(new ProfileMin());
        }
      }

      promises.push(relatedProfilePromise);
    }

    // trigger profile change event after related promises come back
    if (promises && promises.length) {
      Promise.all<any>(promises).then(() => this.onRegistrationProfileChanged.emit(profile));
    }
  };

  public relatedProfileChanged = (profile: ProfileMin): void => {
    if (this.initialLoadComplete == true && profile.ProfileId != this.registration.Registration.CommunicateWithProfileId) {
      this.registration.Registration.CommunicateWithProfileId = profile.ProfileId;

      // if user selected None(0) from the related profile lookup dropdown
      if (profile.ProfileId <= 0) {
        if (!this.registration.Registration.profileOrgInd) {
          this.registration.Registration.OrgName = '';
        } else {
          this.registration.Registration.Prefix = '';
          this.registration.Registration.Title = '';
          this.registration.Registration.FName = '';
          this.registration.Registration.LName = '';
          this.registration.Registration.Email = '';
        }
        return;
      }

      if (profile.OrgInd) {
        this.registration.Registration.OrgName = profile.OrgName;
      } else {
        this.registration.Registration.Prefix = profile.Prefix;
        this.registration.Registration.Title = profile.Title;
        this.registration.Registration.FName = profile.FirstName;
        this.registration.Registration.LName = profile.LastName;
        this.registration.Registration.Email = profile.Email;
      }
    }
  };

  public onOrgNameChange = (evt: any = null): void => {
    // skip empty string
    if (this.registration.Registration.OrgName == '') {
      this.profileOrgDupChecker.hide();
      return;
    }

    if (this.registration.Registration.ProfileId <= 0 && this.registration.Registration.CreateProfile == true) {
      this.profileOrgDupChecker
        .getPossibleDuplicates(this.registration.Registration.OrgName)
        .then((data: PagedResultGeneric<ProfileMin>) => {
          if (data.Result.length > 0) {
            this.profileOrgDupChecker.show();
          } else {
            this.profileOrgDupChecker.hide();
          }
        });
    }
  };

  public onLastNameChange = (): void => {
    // skip empty string
    if (this.registration.Registration.LName == '') {
      this.profileIndDupChecker.hide();
      return;
    }

    if (
      this.registration.Registration.ProfileId <= 0 &&
      this.registration.Registration.CreateProfile == true &&
      this._utilService.isNullEmptyOrWhitespace(this.registration.Registration.OrgName)
    ) {
      let name = this.registration.Registration.LName;
      if (this._utilService.isNotNullEmptyOrWhitespace(this.registration.Registration.FName)) {
        name = this.registration.Registration.FName + ' ' + this.registration.Registration.LName;
      }
      this.profileIndDupChecker.getPossibleDuplicates(name).then((data: PagedResultGeneric<ProfileMin>) => {
        if (data.Result.length > 0) {
          this.profileIndDupChecker.show();
        } else {
          this.profileIndDupChecker.hide();
        }
      });
    }
  };

  public onDupCheckerOrgNameProfileSelected = (data: ProfileMin): void => {
    this.registrationProfileChanged(data, true);
  };

  public onDupCheckerIndNameProfileSelected = (data: ProfileMin): void => {
    this.registrationProfileChanged(data, true);
  };

  public displayField = (value: number): boolean => {
    //1 signup 2 attendee 3 both
    //sign up fields value is 1 or 3
    //attendee is 2 or 3
    //this is sign up so look for 1 or 3
    if (value == 1 || value == 3) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * manually request copying over of profile details
   */
  public handleCopyProfileInfoClick(): void {
    this.registrationProfileChanged(this._lastChosenProfile, true);
  }

  createProfileChanged() {
    this.actionTrackerService.addAction({
      Type: EventRegistationActionTypeEnum.CreateProfileSet,
      NewValue: this.registration.Registration.CreateProfile,
    } as EventRegistrationEditActionDto);
  }
}
